import type { Game } from '@/types';

type RecapEligibleGame = Pick<Game, 'status' | 'homeScore' | 'awayScore' | 'gameRecapURL' | 'sportType'>;

const YOUTUBE_HOSTS = new Set([
  'www.youtube.com',
  'youtube.com',
  'm.youtube.com',
]);

const SCORE_BEARING_RECAP_PATTERN = /\b\d+\s*-\s*\d+\b/;

function parseRecapUrl(recapUrl: string): URL | null {
  try {
    return new URL(recapUrl);
  } catch {
    return null;
  }
}

function isUnsafeSoccerRecapUrl(recapUrl: string): boolean {
  const parsedUrl = parseRecapUrl(recapUrl);
  if (!parsedUrl) {
    return true;
  }

  const host = parsedUrl.hostname.toLowerCase();
  const path = parsedUrl.pathname.toLowerCase();
  const decodedUrlText = decodeURIComponent(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);

  if (SCORE_BEARING_RECAP_PATTERN.test(decodedUrlText)) {
    return true;
  }

  if (YOUTUBE_HOSTS.has(host) && (path === '/results' || path.endsWith('/search'))) {
    return true;
  }

  return false;
}

// Recaps are only product-approved for completed games with final scores.
export function getEligibleRecapUrl(game: RecapEligibleGame): string | null {
  if (
    game.status.type !== 'completed' ||
    game.homeScore === undefined ||
    game.awayScore === undefined
  ) {
    return null;
  }

  if (typeof game.gameRecapURL !== 'string') {
    return null;
  }

  const recapUrl = game.gameRecapURL.trim();
  if (recapUrl.length === 0) {
    return null;
  }

  if (game.sportType === 'soccer' && isUnsafeSoccerRecapUrl(recapUrl)) {
    return null;
  }

  return recapUrl;
}
